import Stack from "react-bootstrap/Stack";
import { Formik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import { FilterProcess } from "./filterProcess";
import { ProcessListTable } from "./processListTable";
import { ProcessoModel } from "../../models/processo.model";
import { QueryBuilder } from "../../models/query.model";
import { fieldTypes } from "../../util/enumerators";
import { ButtonModel } from "../../models/components/button.model";
import { ToastService } from "../../services/toast.service";
import ProcessService from "../../services/process.service";
import {
	FaRegSave,
	FaSave,
	FaPlayCircle,
	FaPlay,
	FaTrashAlt,
	FaTrash,
	FaFileExcel,
	FaRegFileExcel,
} from "react-icons/fa";

import { LuEraser } from "react-icons/lu";
import { FaEraser } from "react-icons/fa";
import { ConfirmationModal } from "../../components/ConfirmationModal";
import { ExportFunctions } from "../../util/ExportFunctions";
import { RequestParamsModel } from "../../models/requestParams.model";
import { entityConfig } from "../../util/entityConfig";

export const ProcessList = () => {
	const formRef = useRef();
	const [showModal, setShowModal] = useState<boolean>(false);
	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const [editingValue, setEditingValue] = useState<string>("");
	const [filterValue, setFilterValue] = useState<QueryBuilder[]>([]);

	// Pooling
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [executionId, setExecutionId] = useState<string | null>(null);
	const pollingRef = useRef<NodeJS.Timeout | null>(null);
	const pollingAttemptsRef = useRef<number>(0);
	const [canExport, setCanExport] = useState<boolean>(false);

	const [initialValues] = useState<ProcessoModel>({
		values: {
			tribunal: "",
			dtInicio: "",
			dtFim: "",
			numProcesso: "",
			salvarQuery: false,
			description: "",
			query: null,
		},
	});
	const [tribunalList, setTribunalList] = useState<any[]>([]);

	const fieldList = [
		{
			field: "tribunal",
			label: "Tribunal",
			type: fieldTypes.SELECT,
			optionsList: tribunalList,
			displayText: "DS_TRIBUNAL",
			value: "ID_TRIBUNAL",
			includeSelectOption: false,
			mdSize: 12,
		},
		{
			field: "numProcesso",
			label: "Nº do Processo",
			placeholder: "Nº do Processo",
			lgSize: 6,
			mdSize: 6,
			maxLength: 60,
		},
	];

	const fieldList2 = [
		{
			field: "description",
			label: "Nuvem de palavras",
			placeholder: "Descrição para pesquisa",
			lgSize: 12,
			mdSize: 12,
			maxLength: 60,
		},
	];

	const buttonsTop: ButtonModel[] = [
		{
			onClick: () => handleSave(),
			text: "Salvar",
			disabled: isLoading,
			icon: (
				<>
					<FaRegSave className="fa-icon" />
					<FaSave className="fa-icon-hover" />
				</>
			),
			tooltip: "Salvar Busca",
			idText: "btn-executar",
		},
		{
			onClick: () => handleSaveAndExecute(),
			text: "Executar",
			disabled: isLoading,
			icon: (
				<>
					<FaPlayCircle className="fa-icon" />
					<FaPlay className="fa-icon-hover" />
				</>
			),
			tooltip: "Salvar e Executar busca",
			idText: "btn-executar",
		},
		{
			onClick: async () => {
				const entity = entityConfig.PROCESS;
				if (!executionId) return;
				await ExportFunctions.downloadExcelWithEndpoint(
					entity.controller,
					RequestParamsModel('MOVIMENTOS'),
					`GetProcesses/${executionId}/ExportExcel`,
					'MOVIMENTOS',
					'processos'
				);
			},
			text: "Exportar",
			disabled: isLoading || !executionId || !canExport,
			icon: (
				<>
					<FaRegFileExcel className="fa-icon" />
					<FaFileExcel className="fa-icon-hover" />
				</>
			),
			tooltip: "Exportar dados para Excel",
			idText: "btn-exportar",
		},
		{
			onClick: () => btnLimparFiltros(),
			text: "Limpar",
			disabled: isLoading,
			icon: (
				<>
					<LuEraser className="fa-icon" />
					<FaEraser className="fa-icon-hover" />
				</>
			),
			tooltip: "Limpar filtros",
			idText: "btn-limparfiltros",
			variant: "warning", // Pode ser secondary, info ou warning
		},
		{
			onClick: () => setShowModal(true),
			text: "Excluir",
			disabled: false,
			icon: (
				<>
					<FaTrashAlt className="fa-icon" />
					<FaTrash className="fa-icon-hover" />
				</>
			),
			tooltip: "Excluir Query",
			idText: "btn-Excluir",
			variant: "danger",
		},
	];

	// Polling
	const startPolling = (id: string) => {
		setExecutionId(id);
		setIsLoading(true);
		if (pollingRef.current) clearInterval(pollingRef.current);
		pollingAttemptsRef.current = 0;

		pollingRef.current = setInterval(async () => {
			try {
				const data: any = await ProcessService.getProcesses(id);
				console.log("🔄 Buscando processos...");
				const ds = data?.dataset?.data?.find((d:any) => d?.idDataset === 'MOVIMENTOS');
				const records = Number(ds?.records ?? 0);
				const list = Array.isArray(data?.result?.MOVIMENTOS) ? data.result.MOVIMENTOS : [];
				const hasSentinelNoResult = list.length === 1 && !list[0]?.ProcessoNumero && String(list[0]?.Detalhamento || '').toLowerCase().includes('nenhum processo encontrado');
				const hasAnyRow = records > 0 || (list.length > 0 && !hasSentinelNoResult);
				if (hasAnyRow || hasSentinelNoResult) {
					stopPolling();
					if (hasAnyRow) {
						ToastService.success("Processamento finalizado.");
					} else {
						ToastService.info("Nenhum processo encontrado para essa busca.", null);
					}
				} else {
					pollingAttemptsRef.current += 1;
					if (pollingAttemptsRef.current >= 60) { // ~5 min (60 * 5s)
						stopPolling();
						ToastService.info("Tempo esgotado aguardando resultados.", null);
					}
				}
			} catch (error) {
				console.error("Erro no polling:", error);
				stopPolling();
				ToastService.error("Erro ao buscar processos.");
			}
		}, 5000); // a cada 5 segundos
	};

	const stopPolling = () => {
		setIsLoading(false);
		if (pollingRef.current) {
			clearInterval(pollingRef.current);
			pollingRef.current = null;
			console.log("🛑 Polling parado.");
		}
	};

	useEffect(() => {
		return () => {
			stopPolling();
		};
	}, []);

	// Removido mock: não definir GUID fake automaticamente

	const addFilter = (condition, values) => {
		let filValue = [...filterValue];
		const last = filValue[filValue.length - 1];
		if (
			filValue.length > 0 &&
			(last.type === "condition" ||
				(last.type === "paren" && last.value === ")"))
		) {
			filValue.push({ type: "operator", value: condition.toUpperCase() });
		}
		if (values.values.description !== "") {
			filValue.push({ type: "condition", value: values.values.description });
		}
		setFilterValue(filValue);
		values.values.description = "";
	};

	const addParentheses = (type: "(" | ")") => {
		const filValue = [...filterValue];
		const balance = filValue.reduce((acc, item) => {
			if (item.type === "paren") {
				if (item.value === "(") return acc + 1;
				if (item.value === ")") return acc - 1;
			}
			return acc;
		}, 0);
		const last = filValue[filValue.length - 1];
		if (type === "(") {
			if (
				last &&
				(last.type === "condition" ||
					(last.type === "paren" && last.value === ")"))
			)
				return;
			filValue.push({ type: "paren", value: "(" });
		} else {
			if (balance <= 0) return;
			if (
				!last ||
				last.type === "operator" ||
				(last.type === "paren" && last.value === "(")
			)
				return;
			filValue.push({ type: "paren", value: ")" });
		}
		setFilterValue(filValue);
	};

	const handleCheckQuery = () => {
		if (!isValidQuery(filterValue)) {
			ToastService.error("Busca NÃO funcional.");
		} else {
			ToastService.success("Busca pronta para ser utilizada.");
		}
	};

    const handleDelete = (index: number) => {
        const updated = [...filterValue];
        updated.splice(index, 1);
        if (isValidQuery(updated) || index === (filterValue.length - 1)) {
            setFilterValue(updated);
        } else {
            ToastService.warn("Remoção inválida. Isso quebraria a estrutura da busca.", null);
        }
    };

	const getBadgeColor = (queryItem: QueryBuilder) => {
		return '';
		switch (queryItem.type) {
			case "condition":
				return "primary";
			case "operator":
				return queryItem.value === "AND" ? "warning" : "danger";
			case "paren":
				return "dark";
			default:
				return "secondary";
		}
	};

	const getIndentLevel = () => 0;

	const btnLimparFiltros = () => {
		setFilterValue([]);
		setExecutionId(null);
		handleClearAll();
		(formRef?.current as any)?.resetForm({
			values: {
				values: {
					tribunal: "",
					dtInicio: "",
					dtFim: "",
					numProcesso: "",
					salvarQuery: false,
					description: "",
					query: null,
				},
			},
		});
	};

	const handleClearAll = () => {
		stopPolling();
	};

	const isValidQuery = (query: QueryBuilder[]) => {
		let balance = 0;
		for (let i = 0; i < query.length; i++) {
			const item = query[i];
			if (item.type === "paren") {
				balance += item.value === "(" ? 1 : -1;
				if (balance < 0) return false;
			}
		}
		return balance === 0;
	};

	const buildPayload = () => {
		const formValues = (formRef.current as any)?.values?.values;

		return {
			queryName: formValues.query,
			tribunal: formValues.tribunal,
			numProcesso: (formValues.numProcesso && String(formValues.numProcesso).trim().length > 0) ? formValues.numProcesso : null,
			dtInicio: formValues.dtInicio,
			dtFim: formValues.dtFim,
			conditions: filterValue,
		};
	};

	const isFormValid = () => {
		const formValues = (formRef.current as any)?.values?.values;

		if (!formValues.query || formValues.query.trim() === "") {
			ToastService.error("Informe ou selecione uma busca.");
			return false;
		}
		// if (!formValues.tribunal) {
		// 	ToastService.error("Selecione o Tribunal.");
		// 	return false;
		// }
		if (!formValues.dtInicio) {
			ToastService.error("Informe a Data de Início.");
			return false;
		}
		if (!formValues.dtFim) {
			ToastService.error("Informe a Data Fim.");
			return false;
		}

		if (filterValue.length === 0) {
			ToastService.error("Adicione pelo menos um filtro na busca.");
			return false;
		}
		if (!isValidQuery(filterValue)) {
			ToastService.error("A busca montada não é válida.");
			return false;
		}

		return true;
	};

	const handleSave = async () => {
		setExecutionId(null);
		setIsLoading(true);
		try {
			if (!isFormValid()) return;
			const payload = buildPayload();

			const response: any = await ProcessService.saveQuery(payload);

			loadQueries();
			ToastService.success("Busca salva com sucesso");
		} catch (error) {
			if (error?.response?.data?.return?.messageError?.message) {
				ToastService.error(error.response.data.return.messageError.message);
			} else {
				ToastService.error("Erro ao salvar busca.");
			}

		} finally {
			setIsLoading(false);
		}
	};

	const handleSaveAndExecute = async () => {
		if (!isFormValid()) return;
		try {
			const payload = buildPayload();

			const response: any = await ProcessService.saveAndExecuteQuery(payload);
			const executionId = response?.result?.executionId;
			loadQueries();

			if (executionId) {
				startPolling(executionId);
			}

			ToastService.success("Busca salva e será executada. Aguarde!");
		} catch (error) {
			console.error("Erro ao salvar e executar busca:", error);
			if (error?.response?.data?.return?.messageError?.message) {
				ToastService.error(error.response.data.return.messageError.message);
			} else {
				ToastService.error("Erro ao salvar e executar busca.");
			}

		}
	};

	const handleDeleteQuery = async () => {
		const formValues = (formRef.current as any)?.values?.values;

		if (!formValues.query) {
			ToastService.info("Nenhuma busca salva para excluir.", null);
			// Só limpa os filtros e processos se não tiver query salva
			btnLimparFiltros();
			return;
		}

		try {
			await ProcessService.deleteQuery(formValues.query);
			ToastService.success("Busca excluída com sucesso.");
			loadQueries();
			btnLimparFiltros();
		} catch (error) {
			console.error("Erro ao excluir Busca:", error);
			if (error?.response?.data?.return?.messageError?.message) {
				ToastService.error(error.response.data.return.messageError.message);
			} else {
				ToastService.error("Erro ao excluir a Busca.");
			}
		}finally{
			setShowModal(false);

		}
	};

	const onSubmit = (data) => console.log("Submit:", data);

	useEffect(() => {
		const loadTribunais = async () => {
			try {
				const apiResponse: any = await ProcessService.getTribunais();
				const mapped = [
					...(apiResponse?.result?.TRIBUNAL || []).map((t: any) => ({
						ID_TRIBUNAL: t.idTribunal,
						DS_TRIBUNAL: t.nome,
					})),
				];
				setTribunalList([
					{ ID_TRIBUNAL: null, DS_TRIBUNAL: 'Selecione um tribunal' }, ...mapped
				]);
			} catch (error) {
				console.error("Erro ao carregar tribunais:", error);
			}
		};

		loadTribunais();
	}, []);

	const [queryList, setQueryList] = useState<any[]>([]);

	const loadQueries = async () => {
		try {
			const apiResponse: any = await ProcessService.getQueries();
			const mapped = (apiResponse?.result?.QUERY || []).map((q: any) => ({
				value: q,
				label: q.queryName,
			}));
			setQueryList(mapped);
			return mapped;
		} catch (error) {
			console.error("Erro ao carregar queries:", error);
			return [];
		}
	};

	useEffect(() => {
		loadQueries();
	}, []);

	 const handleEditSubmit = (index: number) => {
        const updated = [...filterValue];
        const trimmed = editingValue.trim();
        if (!trimmed) return setEditingIndex(null);

        if (updated[editingIndex!].type === 'condition') {
            updated[editingIndex!] = { type: 'condition', value: trimmed };
        } else if (updated[editingIndex!].type === 'operator') {
            const op = trimmed.toUpperCase();
            if (op === 'AND' || op === 'OR') {
                updated[editingIndex!] = { type: 'operator', value: op };
            } else {
                return setEditingIndex(null);
            }
        }
        setFilterValue(updated);
        setEditingIndex(null);
    };
	
	const handleCloseModal = () => {setShowModal(false);}

	return (
		<Row>
			<div className="p-2">
				<Formik
					enableReinitialize
					innerRef={formRef}
					initialValues={initialValues}
					onSubmit={onSubmit}
				>
					{(formikProps) => (
						<Form noValidate onSubmit={formikProps.handleSubmit}>
							<Row>
								<Col sm={12} md={5} lg={3} xl={2} xxl={1}>
									<FilterProcess
										{...formikProps}
										buttonsTop={buttonsTop}
										fieldList={fieldList}
										fieldList2={fieldList2}
										filterValue={filterValue}
										editingIndex={editingIndex}
										editingValue={editingValue}
										handleDelete={handleDelete}
										handleEditSubmit={handleEditSubmit}
										setEditingIndex={setEditingIndex}
										setEditingValue={setEditingValue}
										getBadgeColor={getBadgeColor}
										getIndentLevel={getIndentLevel}
										addFilter={addFilter}
										addParentheses={addParentheses}
										reset={() => setFilterValue([])}
										questionModal={{}}
										setQuestionModal={() => { }}
										handleCheckQuery={handleCheckQuery}
										setFilterValue={setFilterValue}
										handleClearAll={handleClearAll}
										isLoading={isLoading}
										queryList={queryList}
									handleChangeQuery={(id) => {setExecutionId(id)}}
									/>
								</Col>
								<Col sm={12} md={7} lg={9} xl={10} xxl={11}>
									<ProcessListTable isLoading={isLoading} guidQuery={executionId} onHasRowsChange={setCanExport} />
								</Col>
							</Row>
						</Form>
					)}
				</Formik>
			</div>
			 <ConfirmationModal
					show={showModal}
					handleClose={handleCloseModal}
					handleConfirm={handleDeleteQuery}
				  />

		</Row>
	);
};
